import m, {Vnode} from "mithril";
import {NationalDateFormat} from "./DateFormats";
import {Moment} from "moment";
import DateTimeInputBox from "./DateTimeInputBox";
import DateTimeSeparator from "./DateTimeSeparator";
import moment = require("moment");
import Cell from "../../model/form/cell";
import DateTimeReadOnlyBox from "./DateTimeReadOnlyBox";
import DateTimeInputBoxNotNumeric from "./DateTimeInputBoxNotNumeric";

export interface DatePickerOptions {
    minDate?: Moment,
    maxDate?: Moment
}

/**
 * Completing DateTimeInputBoxes into component
 *
 * Example_______________
 * | Day . Month . Year |
 * """"""""""""""""""""""
 */
export default class DatePicker {

    private readonly dateInputBoxes: (DateTimeInputBox|DateTimeReadOnlyBox|DateTimeInputBoxNotNumeric)[] = [];
    private readonly separatorBoxes: DateTimeSeparator[] = [];

    public constructor (
        private _date: Moment|null = null,
        private readonly onDateChangedCallback: (date: Moment) => void,
        public readonly cell: Cell,
        public readonly dateFormat: NationalDateFormat,
        private readonly options: DatePickerOptions = {}
    ) {

        for (const i in this.dateFormat.format) {
            const format: string = this.dateFormat.format[i];
            const placeholder: string = this.dateFormat.placeholders[i];
            const separator: string | undefined = this.dateFormat.separators[i];
            const readonly = this.dateFormat.readonly ? this.dateFormat.readonly[i] : false;
            const numeric = this.dateFormat.numeric ? this.dateFormat.numeric[i] : true;

            let dateInputBox;
            if (readonly) {
                dateInputBox = new DateTimeReadOnlyBox(
                    this,
                    this.createFormatter(format, placeholder).bind(this),
                );
            } else if (!numeric) {
                dateInputBox = new DateTimeInputBoxNotNumeric(
                    this,
                    this.createFormatter(format, placeholder).bind(this),
                    this.createModifier(format).bind(this),
                    {
                        minDate: this.options.minDate,
                        maxDate: this.options.maxDate,
                        maxlength: format.length,
                    }
                );
            } else {
                dateInputBox = new DateTimeInputBox(
                    this,
                    this.createFormatter(format, placeholder).bind(this),
                    this.createModifier(format).bind(this),
                    this.createSetter(format).bind(this),
                    {
                        minDate: this.options.minDate,
                        maxDate: this.options.maxDate,
                        maxlength: format.length,
                        min: 0,
                        step: 1,
                        max: Number(format.replace(/./gi, '9')),
                    }
                );
            }

            this.dateInputBoxes.push(dateInputBox);
            if (separator) {
                this.separatorBoxes.push(new DateTimeSeparator(separator));
            }

        }
    }

    public get date(): Moment|null {
        return this._date;
    }

    public set date(date: Moment|null) {
        this._date = date;
        if (date) {
            this.onDateChangedCallback(date);
        }
    }


    /**
     * @return Function responsible for formatting one piece of date
     */
    private createFormatter(format: string, placeholder: string) {
        return (date: Moment|null) => {
            if (date === null || !this.cell.enabled) {
                return placeholder;
            } else  {
                return date.format(format);
            }
        }
    }

    /**
     * @return Function responsible for modifying one piece of date
     */
    private createModifier(format: string) {
        return (date: Moment|null, n: number) => {
            if (date === null) {
                date = moment();
            }
            switch(format) {
                case 'A':
                    return date.add(12, 'hours');
                case 'mm':
                    return date.add(n, 'minutes');
                case 'hh':
                    return date.add(n, 'hours');
                case 'HH':
                    return date.add(n, 'hours');
                case 'YYYY':
                    return date.add(n, 'years');
                case 'MM':
                    return date.add(n, 'months');
                default:
                    return date.add(n, 'days');
            }
        }
    }

    /**
     * @return Function responsible for set one piece of date
     */
    private createSetter(format: string) {
        return (date: Moment|null, n: number) => {
            if (date === null) {
                date = moment();
            }
            switch(format) {
                case 'A':
                    return date; // do not modify AM/PM
                case 'mm':
                    return date.minute(n);
                case 'HH':
                    return date.hour(n);
                case 'hh':
                    return date.hour(n);
                case 'YYYY':
                    return date.year(n);
                case 'MM':
                    return date.month(n-1); // https://github.com/denysdovhan/wtfjs
                default:
                    return date.date(n);
            }
        }
    }


    public view(): Vnode|null {
        if (!this.cell.visible) {
            return null;
        }

        const boxes = [];
        for (const i in this.dateInputBoxes) {
            boxes.push(m(this.dateInputBoxes[i]));
            if (this.separatorBoxes[i]) {
                boxes.push(m(this.separatorBoxes[i]));
            }
        }

        return m('.datepicker'+(this.cell.enabled ? '':'.disabled'), {}, boxes);
    }
}